import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { OperatorMark } from '../brand/OperatorMark';
import { runStateStore } from '../../hooks/useAsyncInvokeAdapter';
import { formatElapsed } from '../../utils/format';
import { thinkingHintForQuietMs } from '../../utils/thinking-hints';

/**
 * Resolves a run's elapsed seconds for the "Thought for Xs" header.
 *
 * While the run is working it ticks a client-side timer; once the message
 * reaches a terminal state it freezes to the authoritative duration — preferring
 * the per-message metadata (restored conversations) then the per-runId run-state
 * store (live runs), falling back to the client timer.
 *
 * Shared by {@link AgentChainOfThought} and the single-turn fallback header so
 * both report identical timings.
 */
export function useRunElapsedSeconds(isWorking: boolean): number {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const custom = messageMetadata?.custom as Record<string, unknown> | undefined;
  const perMsgRunId = custom?.runId as string | undefined;

  const subscribeRun = useCallback(
    (listener: () => void) => runStateStore.subscribe(perMsgRunId, listener),
    [perMsgRunId],
  );
  const getRunSnapshot = useCallback(() => runStateStore.getSnapshot(perMsgRunId), [perMsgRunId]);
  const runSnapshot = useSyncExternalStore(subscribeRun, getRunSnapshot);

  // Per-message duration from metadata (set during conversation restore)
  const perMsgMs = custom?.runDurationMs as number | undefined;
  // Stable per-runId duration from the run state store (live runs)
  const runStoreDurationMs = runSnapshot.durationMs;

  // Timer origin: the run's store-stamped start, NOT this component's mount, so
  // a header that mounts late (e.g. the single-turn fallback, which only renders
  // once the answer streams) still reads the elapsed-since-run-start rather than
  // restarting from ~0. Falls back to mount time when the store has no slot.
  const mountTimeRef = useRef(Date.now());
  const origin = runSnapshot.startedAt ?? mountTimeRef.current;
  const [elapsed, setElapsed] = useState(0);
  const frozenElapsedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isWorking) {
      if (frozenElapsedRef.current === null) {
        if (perMsgMs != null) {
          frozenElapsedRef.current = Math.round(perMsgMs / 1000);
        } else if (runStoreDurationMs != null) {
          frozenElapsedRef.current = Math.round(runStoreDurationMs / 1000);
        } else {
          frozenElapsedRef.current = Math.round((Date.now() - origin) / 1000);
        }
      }
      setElapsed(frozenElapsedRef.current);
      return;
    }
    frozenElapsedRef.current = null;
    const timer = setInterval(() => {
      setElapsed(Math.round((Date.now() - origin) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [isWorking, perMsgMs, runStoreDurationMs, origin]);

  return frozenElapsedRef.current ?? elapsed;
}

/**
 * Escalating hint label once a working run has produced no new live activity
 * for a while, or null while events are flowing. Long silent stretches are
 * normal — a model at high reasoning effort can think for tens of seconds
 * before its first tool call, and the runtime streams no reasoning deltas
 * while it does — so the header reassures instead of looking stalled. Resets
 * whenever `liveVersion` bumps (every run event updates the live snapshot).
 */
export function useThinkingHint(isWorking: boolean, liveVersion: number): string | null {
  const [hint, setHint] = useState<string | null>(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    lastActivityRef.current = Date.now();
    setHint(null);
  }, [liveVersion]);

  useEffect(() => {
    if (!isWorking) {
      setHint(null);
      return;
    }
    const timer = setInterval(() => {
      setHint(thinkingHintForQuietMs(Date.now() - lastActivityRef.current));
    }, 1000);
    return () => clearInterval(timer);
  }, [isWorking]);

  return hint;
}

/** Shared base classes for the collapsed (non-interactive) thought header row. */
export const COT_HEADER_BASE_CLASS =
  'flex w-full items-center gap-2 rounded py-1 text-sm text-muted-foreground';

interface ThoughtHeaderContentProps {
  /** True while the run is still working — shows the breathing label + live timer. */
  working: boolean;
  /** Label shown while working (e.g. a playful gerund or "Thinking..."). */
  label: string;
  /** Elapsed seconds; rendered as the frozen "Thought for Xs" once done. */
  elapsedSeconds: number;
  /** Quiet-time escalation fragment appended to the trailing metadata while
   *  working, e.g. `(18s · still thinking)`. Null/omitted → `(18s)` only. */
  hint?: string | null;
}

/**
 * The inner content of the "Thought for Xs" header — shared between the
 * expandable chain-of-thought header and the single-turn fallback header so the
 * mark, label, and timing read identically everywhere.
 */
export function ThoughtHeaderContent({
  working,
  label,
  elapsedSeconds,
  hint,
}: ThoughtHeaderContentProps) {
  return (
    <>
      <OperatorMark
        className={`h-4 w-4 shrink-0 ${working ? 'text-foreground animate-cot-breathe' : 'text-foreground/60'}`}
      />
      <span className={working ? 'text-foreground animate-cot-breathe' : ''}>
        {working ? label : `Thought for ${formatElapsed(elapsedSeconds)}`}
      </span>
      {working && elapsedSeconds > 0 && (
        <span className="text-xs font-normal tabular-nums text-muted-foreground">
          {formatElapsed(elapsedSeconds)}
          {hint ? ` · ${hint}` : ''}
        </span>
      )}
    </>
  );
}
